import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaEnvelope, FaEnvelopeOpen, FaTrash, FaReply, FaCheck } from 'react-icons/fa';
import { db, collection, getDocs, doc, updateDoc, deleteDoc, query, orderBy } from '../../firebase';

const SubmissionsManager = () => {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, unread, read
  const [selectedSubmission, setSelectedSubmission] = useState(null);

  useEffect(() => {
    fetchSubmissions();
  }, []);

  const fetchSubmissions = async () => {
    try {
      setLoading(true);
      const q = query(collection(db, 'submissions'), orderBy('timestamp', 'desc'));
      const querySnapshot = await getDocs(q);
      
      const submissionsData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setSubmissions(submissionsData);
    } catch (error) {
      console.error('Error fetching submissions:', error);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (id) => {
    try {
      await updateDoc(doc(db, 'submissions', id), {
        status: 'read',
        readAt: new Date().toISOString()
      });
      
      setSubmissions(prev =>
        prev.map(sub => sub.id === id ? { ...sub, status: 'read' } : sub)
      );
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Are you sure you want to delete this submission?')) return;

    try {
      await deleteDoc(doc(db, 'submissions', id));
      setSubmissions(prev => prev.filter(sub => sub.id !== id));
      if (selectedSubmission?.id === id) {
        setSelectedSubmission(null);
      }
      alert('Submission deleted successfully!');
    } catch (error) {
      console.error('Error deleting submission:', error);
      alert('Error deleting submission. Please try again.');
    }
  };

  const handleReply = (email) => {
    window.location.href = `mailto:${email}`;
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Unknown date';
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredSubmissions = submissions.filter(sub => {
    if (filter === 'all') return true;
    return sub.status === filter;
  });

  const unreadCount = submissions.filter(sub => sub.status === 'unread').length;

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-white">Contact Form Submissions</h2>
          <p className="text-gray-400 mt-1">
            {submissions.length} total submissions {unreadCount > 0 && (
              <span className="text-violet-400">• {unreadCount} unread</span>
            )}
          </p>
        </div>
        
        {/* Filter Tabs */}
        <div className="flex gap-2">
          <button
            onClick={() => setFilter('all')}
            className={`px-4 py-2 rounded-lg transition-all ${
              filter === 'all'
                ? 'bg-violet-500 text-white'
                : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
            }`}
          >
            All ({submissions.length})
          </button>
          <button
            onClick={() => setFilter('unread')}
            className={`px-4 py-2 rounded-lg transition-all ${
              filter === 'unread'
                ? 'bg-violet-500 text-white'
                : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
            }`}
          >
            Unread ({unreadCount})
          </button>
          <button
            onClick={() => setFilter('read')}
            className={`px-4 py-2 rounded-lg transition-all ${
              filter === 'read'
                ? 'bg-violet-500 text-white'
                : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
            }`}
          >
            Read ({submissions.length - unreadCount})
          </button>
        </div>
      </div>

      {filteredSubmissions.length === 0 ? (
        <div className="text-center py-16 bg-gray-800 rounded-lg">
          <FaEnvelope className="mx-auto text-6xl text-gray-600 mb-4" />
          <p className="text-gray-400">No submissions yet.</p>
          <p className="text-gray-500 text-sm mt-2">
            {filter === 'unread' ? 'All caught up!' : 'Your contact form submissions will appear here.'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Submissions List */}
          <div className="lg:col-span-1 space-y-2 max-h-[600px] overflow-y-auto">
            <AnimatePresence>
              {filteredSubmissions.map((submission) => (
                <motion.div
                  key={submission.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  onClick={() => {
                    setSelectedSubmission(submission);
                    if (submission.status === 'unread') {
                      markAsRead(submission.id);
                    }
                  }}
                  className={`p-4 rounded-lg cursor-pointer transition-all ${
                    selectedSubmission?.id === submission.id
                      ? 'bg-violet-500 text-white'
                      : submission.status === 'unread'
                      ? 'bg-gray-800 hover:bg-gray-700 border-l-4 border-violet-500'
                      : 'bg-gray-800 hover:bg-gray-700'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        {submission.status === 'unread' ? (
                          <FaEnvelope className="text-violet-400" />
                        ) : (
                          <FaEnvelopeOpen className="text-gray-500" />
                        )}
                        <span className="font-semibold truncate">{submission.name}</span>
                      </div>
                      <p className="text-sm opacity-75 truncate mt-1">{submission.email}</p>
                      <p className="text-xs opacity-60 mt-1">{formatDate(submission.timestamp)}</p>
                    </div>
                  </div>
                  <p className="text-sm mt-2 line-clamp-2 opacity-80">
                    {submission.message}
                  </p>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>

          {/* Submission Detail */}
          <div className="lg:col-span-2">
            {selectedSubmission ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-gray-800 rounded-lg p-6"
              >
                <div className="flex justify-between items-start mb-6">
                  <div>
                    <h3 className="text-2xl font-bold text-white">{selectedSubmission.name}</h3>
                    <p className="text-violet-400 mt-1">{selectedSubmission.email}</p>
                    <p className="text-gray-500 text-sm mt-1">
                      {formatDate(selectedSubmission.timestamp)}
                    </p>
                  </div>
                  
                  <div className="flex gap-2">
                    {selectedSubmission.status === 'unread' && (
                      <button
                        onClick={() => markAsRead(selectedSubmission.id)}
                        className="p-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-all"
                        title="Mark as read"
                      >
                        <FaCheck />
                      </button>
                    )}
                    <button
                      onClick={() => handleReply(selectedSubmission.email)}
                      className="p-2 bg-violet-500 text-white rounded-lg hover:bg-violet-600 transition-all"
                      title="Reply"
                    >
                      <FaReply />
                    </button>
                    <button
                      onClick={() => handleDelete(selectedSubmission.id)}
                      className="p-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-all"
                      title="Delete"
                    >
                      <FaTrash />
                    </button>
                  </div>
                </div>

                <div className="bg-gray-900 rounded-lg p-6">
                  <h4 className="text-gray-400 text-sm font-semibold mb-3 uppercase">Message</h4>
                  <p className="text-white text-lg leading-relaxed whitespace-pre-wrap">
                    {selectedSubmission.message}
                  </p>
                </div>

                <div className="mt-6 flex gap-3">
                  <button
                    onClick={() => handleReply(selectedSubmission.email)}
                    className="flex-1 flex items-center justify-center gap-2 bg-gradient-to-r from-violet-500 to-purple-500 text-white px-6 py-3 rounded-lg hover:shadow-lg transition-all"
                  >
                    <FaReply />
                    Reply to {selectedSubmission.name}
                  </button>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(selectedSubmission.email);
                      alert('Email copied to clipboard!');
                    }}
                    className="px-6 py-3 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-all"
                  >
                    Copy Email
                  </button>
                </div>
              </motion.div>
            ) : (
              <div className="bg-gray-800 rounded-lg p-12 text-center">
                <FaEnvelope className="mx-auto text-6xl text-gray-600 mb-4" />
                <p className="text-gray-400">Select a submission to view details</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-gray-800 p-4 rounded-lg">
          <div className="text-gray-400 text-sm">Total Submissions</div>
          <div className="text-2xl font-bold text-white mt-1">{submissions.length}</div>
        </div>
        <div className="bg-gray-800 p-4 rounded-lg">
          <div className="text-gray-400 text-sm">Unread</div>
          <div className="text-2xl font-bold text-violet-400 mt-1">{unreadCount}</div>
        </div>
        <div className="bg-gray-800 p-4 rounded-lg">
          <div className="text-gray-400 text-sm">Response Rate</div>
          <div className="text-2xl font-bold text-green-400 mt-1">
            {submissions.length > 0
              ? Math.round(((submissions.length - unreadCount) / submissions.length) * 100)
              : 0}%
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubmissionsManager;
